import { useEffect, useState } from "react";
import { Loader2, Play } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Reveal } from "@/components/Reveal";
import { GreekWatermark } from "@/components/GreekWatermark";
import { api } from "@/lib/api";

export default function Vlog() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api
      .get("/vlog")
      .then(({ data }) => setVideos(data))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="pt-28 sm:pt-36 pb-20 sm:pb-28 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
      <GreekWatermark letter="Ω" className="top-8 right-0" />
      <Reveal>
        <p className="eyebrow mb-3">Behind the reps</p>
        <h1 className="font-anton uppercase tracking-tighter text-cream text-4xl sm:text-6xl">The Vlog</h1>
        <p className="text-smoke mt-4 max-w-lg">Games recaps, coaching breakdowns, and member spotlights.</p>
      </Reveal>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-8 w-8 text-gold animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
          {videos.map((v, i) => (
            <Reveal key={v.id} delay={i * 0.05}>
              <button
                onClick={() => setActive(v)}
                className="group block w-full text-left border border-line bg-surface hover:border-gold/50 transition-colors"
                data-testid={`vlog-card-${v.id}`}
              >
                <div className="relative h-52 overflow-hidden">
                  <img src={v.thumbnail} alt={v.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-void/40 flex items-center justify-center">
                    <div className="w-14 h-14 rounded-full bg-gold/90 flex items-center justify-center group-hover:bg-gold transition-colors">
                      <Play className="h-6 w-6 text-void" />
                    </div>
                  </div>
                  {v.duration && <span className="absolute bottom-2 right-2 bg-void/80 text-cream text-xs px-2 py-0.5">{v.duration}</span>}
                </div>
                <div className="p-5">
                  <h3 className="font-anton uppercase text-cream text-lg leading-tight">{v.title}</h3>
                  <p className="text-smoke text-sm mt-2 leading-relaxed">{v.description}</p>
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      )}

      <Dialog open={!!active} onOpenChange={(open) => !open && setActive(null)}>
        <DialogContent className="bg-void border border-gold/30 max-w-3xl p-2 sm:p-4 rounded-none">
          <DialogTitle className="sr-only">{active?.title}</DialogTitle>
          {active && (
            <div className="aspect-video w-full">
              {active.video_url ? (
                <video
                  src={active.video_url}
                  controls
                  autoPlay
                  className="w-full h-full"
                  data-testid="vlog-video-player"
                />
              ) : (
                <iframe
                  className="w-full h-full"
                  src={`https://www.youtube.com/embed/${active.youtube_id}?autoplay=1`}
                  title={active.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  data-testid="vlog-video-player"
                />
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
